import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSeason } from '../../context/SeasonContext';
import { useCurrentWeek } from '../../context/CurrentWeekContext';
import { useGameContext } from '../../context/GameContext';
import { getCurrentWeek } from '../../utils/dateUtils';
import api from '../../api';
import SettingsSharpIcon from '@mui/icons-material/SettingsSharp';
import MobileFilterButton from './MobileFilterButton';
import MobileFeedbackButton from './MobileFeedbackButton';
import styles from '../../styles/MobileGlobalHeader.module.css';

const MobileGlobalHeader = () => {
  const { user, logout } = useAuth();
  const { adminOverride, effectiveSeason } = useSeason();
  const { currentWeek, calendar } = useCurrentWeek();
  const { rankingsInfo, games } = useGameContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isWeekSelectorOpen, setIsWeekSelectorOpen] = useState(false);
  const [displayedWeek, setDisplayedWeek] = useState(currentWeek);
  const [showPlayoffTab, setShowPlayoffTab] = useState(false);

  useEffect(() => {
    const checkPlayoff = async () => {
      if (user) {
        try {
          const response = await api.getPlayoffStatus(effectiveSeason);
          // Show tab for active or completed playoffs (so users can see results)
          setShowPlayoffTab(response.data.exists && (response.data.playoff.status === 'active' || response.data.playoff.status === 'completed'));
        } catch (err) {
          setShowPlayoffTab(false);
        }
      }
    };
    checkPlayoff();
    const interval = setInterval(checkPlayoff, 30000);
    return () => clearInterval(interval);
  }, [user, effectiveSeason]);

  // Calculate the actual current week based on calendar and today's date
  const actualCurrentWeek = useMemo(() => {
    return getCurrentWeek(calendar);
  }, [calendar]);

  // Listen for week change events from the weekly picks page
  useEffect(() => {
    const handleWeekChange = (event) => {
      setDisplayedWeek(event.detail.week);
    };

    window.addEventListener('weekChange', handleWeekChange);
    return () => window.removeEventListener('weekChange', handleWeekChange);
  }, []);

  // Update displayedWeek when currentWeek changes (initial load)
  useEffect(() => {
    if (currentWeek && !displayedWeek) {
      setDisplayedWeek(currentWeek);
    }
  }, [currentWeek, displayedWeek]);

  const isDevEnvironment = window.location.hostname === 'dev.lotwleague.com';

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
  };

  const handleMenuToggle = useCallback(() => {
    setIsMenuOpen(!isMenuOpen);
    if (isWeekSelectorOpen) setIsWeekSelectorOpen(false);
  }, [isMenuOpen, isWeekSelectorOpen]);

  const handleWeekSelectorToggle = useCallback(() => {
    setIsWeekSelectorOpen(!isWeekSelectorOpen);
    if (isMenuOpen) setIsMenuOpen(false);
  }, [isWeekSelectorOpen, isMenuOpen]);

  const handleWeekSelect = useCallback((week) => {
    // DO NOT update the current week in the context - that should only be the calendar-based current week
    // Only dispatch a custom event to notify the weekly picks page to change the displayed week
    const event = new CustomEvent('weekChange', {
      detail: { week: week.week }
    });
    window.dispatchEvent(event);
    setIsWeekSelectorOpen(false);
  }, []);

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Handle escape key to close menu
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isMenuOpen) {
        closeMenu();
      }
    };

    if (isMenuOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isMenuOpen]);



  return (
    <header className={styles.mobileHeader}>
      {/* Dev Environment Banner */}
      {isDevEnvironment && (
        <div className={styles.devBanner}>
          DEV ENVIRONMENT
        </div>
      )}

      {/* Admin Override Indicator */}
      {adminOverride && (
        <div className={styles.adminOverrideBanner}>
          {adminOverride} Archive
        </div>
      )}

      <div className={`${styles.headerBar} ${location.pathname === '/weekly-picks' ? styles.weeklyPicks : ''}`}>
        {/* Hamburger Menu Button */}
        <button
          className={styles.hamburgerButton}
          onClick={handleMenuToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleMenuToggle();
            }
          }}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
          aria-controls="mobile-menu"
        >
          <span className={styles.hamburgerIcon}></span>
        </button>

        {/* Week Selector (only on weekly-picks page) */}
        {location.pathname === '/weekly-picks' && (
          <button
            className={`${styles.weekSelector} ${styles.weekSelectorButton}`}
            onClick={handleWeekSelectorToggle}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleWeekSelectorToggle();
              }
            }}
            aria-label="Select week"
            aria-expanded={isWeekSelectorOpen}
            aria-haspopup="listbox"
          >
            Week {displayedWeek || currentWeek || 1}
          </button>
        )}

        {/* App Title (only show on non-weekly-picks pages) */}
        {location.pathname !== '/weekly-picks' && (
          <div className={styles.appTitle}>
            LOTW League
          </div>
        )}

        {/* Right side buttons container */}
        <div className={styles.rightButtons}>
          {/* Filter Button (only on weekly-picks page) */}
          {location.pathname === '/weekly-picks' && (
            <div className={styles.filterButton}>
              <MobileFilterButton games={games} />
            </div>
          )}

          {/* Feedback Button */}
          <MobileFeedbackButton />
        </div>
      </div>

      {/* Rankings Info Banner */}
      {rankingsInfo && !rankingsInfo.hasRankings && (
        <div className={styles.rankingsBanner}>
          <span className={styles.rankingsMessage}>
            Rankings Not Yet Available for Week {rankingsInfo.week}
          </span>
        </div>
      )}
      {rankingsInfo && rankingsInfo.hasRankings && rankingsInfo.isFallback && (
        <div className={styles.rankingsBanner} style={{ backgroundColor: '#2d4a2d' }}>
          <span className={styles.rankingsMessage}>
            Showing Final CFP Rankings
          </span>
        </div>
      )}

      {/* Week Selector Dropdown */}
      {isWeekSelectorOpen && (
        <div
          className={styles.weekSelectorDropdown}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setIsWeekSelectorOpen(false);
            }
          }}
          role="listbox"
          aria-label="Week selection"
        >
          {calendar?.filter(week => !week.hide_week).map(week => (
            <button
              key={week.week}
              className={`${styles.weekOption} ${week.week === actualCurrentWeek ? styles.currentWeek : ''} ${week.week === displayedWeek ? styles.selectedWeek : ''}`}
              onClick={() => handleWeekSelect(week)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleWeekSelect(week);
                }
              }}
              role="option"
              aria-selected={week.week === displayedWeek}
            >
              {week.season_type === 'postseason' && week.week >= 17
                ? `BW${week.week - 16}`
                : `Week ${week.week}`}
            </button>
          ))}
        </div>
      )}

      {/* Dropdown Menu */}
      {isMenuOpen && (
        <div
          className={styles.menuOverlay}
          onClick={closeMenu}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              closeMenu();
            }
          }}
          role="dialog"
          aria-label="Menu overlay"
        >
          <div
            id="mobile-menu"
            className={styles.menuContent}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                closeMenu();
              }
            }}
            role="document"
          >
            {/* Menu Header with User Info */}
            <div className={styles.menuHeader}>
              <div className={styles.userGreeting}>Hello, {user?.username}</div>
              <Link to="/user-settings" className={styles.settingsButton} onClick={closeMenu}>
                <SettingsSharpIcon className={styles.settingsIcon} />
              </Link>
            </div>

            {/* Navigation Links */}
            <nav className={styles.menuNav}>
              <Link to="/" className={styles.menuLink} onClick={closeMenu}>
                Home
              </Link>
              {user && (
                <Link to="/weekly-picks" className={styles.menuLink} onClick={closeMenu}>
                  Weekly Picks
                </Link>
              )}
              {user && (
                <Link to="/leaderboard" className={styles.menuLink} onClick={closeMenu}>
                  League Stats
                </Link>
              )}
              {user && showPlayoffTab && (
                <Link to="/playoff" className={styles.menuLink} onClick={closeMenu}>
                  🏆 League Playoff
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link to="/admin" className={styles.menuLink} onClick={closeMenu}>
                  Admin Dashboard
                </Link>
              )}
            </nav>

            {/* Logout Button */}
            <button onClick={handleLogout} className={styles.logoutButton}>
              Logout
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default MobileGlobalHeader;
